import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Mascot, mascotPose, type MascotScenario, type MascotPose } from './brand';
import { Button } from './controls';
import { useT } from '../i18n';

interface Toast { id: number; message: ReactNode; action?: { label: string; onClick: () => void }; duration: number; focusAction?: boolean }
interface ToastApi { show(message: ReactNode, opts?: { action?: Toast['action']; duration?: number; focusAction?: boolean }): number; dismiss(id: number): void }
const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);
  const dismiss = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);
  const show = useCallback<ToastApi['show']>((message, opts = {}) => {
    const id = ++seq.current;
    const toast: Toast = { id, message, action: opts.action, duration: opts.duration ?? 5000, focusAction: opts.focusAction };
    setToasts((t) => [...t.slice(-2), toast]);
    if (!toast.action && toast.duration > 0) setTimeout(() => dismiss(id), toast.duration);
    return id;
  }, [dismiss]);
  return (
    <ToastContext.Provider value={{ show, dismiss }}>
      {children}
      <div className="dz-toasts" role="status" aria-live="polite">
        {toasts.map((t) => <ToastItem key={t.id} toast={t} dismiss={dismiss} />)}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, dismiss }: { toast: Toast; dismiss: ToastApi['dismiss'] }) {
  const action = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const button = action.current;
    if (!button || !button.parentElement) return;
    const item = button.parentElement;
    const active = item.ownerDocument.activeElement;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let focused = item.contains(active);
    let hovered = item.matches(':hover');
    function pause() { clearTimeout(timer); }
    function expire() { dismiss(toast.id); }
    function restart() {
      pause();
      if (!focused && !hovered && toast.duration > 0) timer = setTimeout(expire, toast.duration);
    }
    function focus() { focused = true; pause(); }
    function blur(event: FocusEvent) { focused = item.contains(event.relatedTarget as Node | null); restart(); }
    function enter() { hovered = true; pause(); }
    function leave() { hovered = false; restart(); }
    item.addEventListener('focusin', focus);
    item.addEventListener('focusout', blur);
    item.addEventListener('pointerenter', enter);
    item.addEventListener('pointerleave', leave);
    const frame = requestAnimationFrame(() => {
      if (toast.focusAction) button.focus();
    });
    restart();
    return () => {
      cancelAnimationFrame(frame);
      pause();
      item.removeEventListener('focusin', focus);
      item.removeEventListener('focusout', blur);
      item.removeEventListener('pointerenter', enter);
      item.removeEventListener('pointerleave', leave);
    };
  }, [toast, dismiss]);
  function runAction() { toast.action?.onClick(); dismiss(toast.id); }
  return (
    <div className="dz-toast">
      <span>{toast.message}</span>
      {toast.action && <button ref={action} type="button" onClick={runAction}>{toast.action.label}</button>}
    </div>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

export function EmptyState({ pose, scene, title, children, action }: { pose?: MascotPose; scene?: MascotScenario; title: ReactNode; children?: ReactNode; action?: ReactNode }) {
  const art = scene ? mascotPose(scene) : pose;
  return (
    <div className="dz-empty">
      {art && <Mascot pose={art} width={160} />}
      <h2>{title}</h2>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

/** Native <dialog> wrapper: focus trapping, Esc and backdrop come from the platform.
 * React bubbles close events; a nested confirmation must never close its parent dialog. */
export function Dialog({ open, onClose, title, children, actions, size }: { open: boolean; onClose: () => void; title: ReactNode; children?: ReactNode; actions?: ReactNode; /** 'wide' for tables (the shortcuts panel). */ size?: 'wide' }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const d = ref.current; if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  // A click on the backdrop lands on the <dialog> itself and closes it, like Esc (the native cancel). It is a
  // mouse shortcut for a key the platform already handles, so it stays a listener on the dialog, not a JSX handler.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const d = ref.current; if (!d) return;
    const backdrop = (e: MouseEvent) => { if (e.target === d) closeRef.current(); };
    d.addEventListener('click', backdrop);
    return () => d.removeEventListener('click', backdrop);
  }, []);
  return (
    <dialog ref={ref} className={`dz-dialog${size === 'wide' ? ' dz-dialog--wide' : ''}`} onClose={(event) => { event.stopPropagation(); onClose(); }} aria-labelledby={titleId}>
      {/* Title and actions stay put; only the content between them scrolls. */}
      <div className="dz-dialog__head"><h2 id={titleId} style={{ fontSize: 'var(--dz-text-lg)' }}>{title}</h2></div>
      <div className="dz-dialog__body">{children}</div>
      {actions && <div className="dz-dialog__actions">{actions}</div>}
    </dialog>
  );
}

/** Ask before an important action. Cancel is focused first, so Enter never confirms by accident. */
export function ConfirmDialog({ open, onClose, onConfirm, title, children, confirmLabel, icon }: {
  open: boolean; onClose(): void; onConfirm(): Promise<unknown> | void; title: ReactNode; children?: ReactNode; confirmLabel: string; icon?: ReactNode;
}) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const confirm = async () => { setBusy(true); try { await onConfirm(); } finally { setBusy(false); } };
  return (
    <Dialog open={open} onClose={onClose} title={title} actions={<>
      <Button variant="ghost" onClick={onClose} disabled={busy}>{t('ui.cancel')}</Button>
      <Button variant="primary" icon={icon} loading={busy} onClick={confirm}>{confirmLabel}</Button>
    </>}>{children}</Dialog>
  );
}
